import { PolymorphicButtonBase } from '@plato/ui/primitives/button';
import { cn } from '@plato/ui/util/classes';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PlatoBust } from '../avatar/plato-bust.tsx';
import { useAskPlato } from './ask-plato-provider.tsx';
import { AskPlatoConnectedTeaser } from './ask-plato-teaser.tsx';

/**
 * Desktop entry point: the round Ask Plato FAB in the bottom-right corner,
 * with the teaser bubble and the unread dot until the chat is first opened.
 * The chat popover anchors above it; while it is open the FAB closes it again.
 * On mobile the entry point is `AskPlatoMobileTrigger` in the top bar instead.
 */
export function AskPlatoFab(): ReactElement {
    const { t } = useTranslation('ask-plato');
    const { open, openAskPlato, closeAskPlato, showTeaser, speaking } = useAskPlato();

    return (
        <div
            className={cn(
                // The column only anchors; the teaser and button take their own
                // pointer events so the corner stays clickable around them.
                'pointer-events-none fixed right-4 bottom-3 z-45 hidden flex-col items-end gap-cozy sm:flex',
            )}
        >
            <AskPlatoConnectedTeaser tail="down" className="pointer-events-auto" />
            <PolymorphicButtonBase
                aria-label={open ? t('close') : t('trigger')}
                onClick={() => {
                    if (open) {
                        closeAskPlato();
                    } else {
                        openAskPlato('fab');
                    }
                }}
                className={cn(
                    'pointer-events-auto relative size-13 cursor-pointer rounded-full select-none',
                    // Navy-tinted lift that sits with the blue disc, plus a tight contact shadow.
                    'shadow-[0_6px_16px_rgb(27_47_122/0.26),0_2px_4px_rgb(0_0_0/0.1)]',
                )}
            >
                <PlatoBust speaking={speaking} blink={open} follow />
                {showTeaser && (
                    <span
                        aria-hidden
                        className="absolute top-0 right-0 size-3 rounded-full bg-danger ring-2 ring-container"
                    />
                )}
            </PolymorphicButtonBase>
        </div>
    );
}
